import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@context/AuthContext";
import axios from "axios";
import { API_BASE, PROFILE_IMG_BASE, FIRM_IMG_BASE } from "@config/env";
import { Modal, ModalFooter } from "@common/Modal";



const post = (ep, f) => { const fd=new FormData(); Object.entries(f).forEach(([k,v])=>{ if(v!=null) fd.append(k,v); }); return axios.post(`${API_BASE}${ep}`,fd).then(r=>r.data).catch(()=>null); };
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-US",{year:"numeric",month:"long",day:"numeric"}); } catch { return d||""; } };
const JOB_TYPE = { "1":"Full-time","2":"Part-time","3":"Contract","4":"Permanent" };

// ── Status colors ─────────────────────────────────────────────────────────────
const STATUS_STYLE = {
  "1": { bg:"var(--fc-red-light)", color:"#DC2626", label:"Shortlisted" },
  "2": { bg:"var(--fc-amber-light)", color:"#D97706", label:"Interview"   },
  "3": { bg:"var(--fc-green-light)", color:"#16A34A", label:"Presented"   },
  "4": { bg:"var(--fc-border-light)", color:"var(--fc-text-muted)", label:"Rejected"    },
};

// ── Section item ──────────────────────────────────────────────────────────────

import JobOverviewTab    from "@features/jobs/JobOverviewTab";
import JobCandidatesTab  from "@features/jobs/JobCandidatesTab";
import JobAIMatchingTab  from "@features/jobs/JobAIMatchingTab";

export default function JobDetailPage() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { user, userSlug } = useAuth();

  const user_id  = user?.user_id;
  const firmId   = user?.firm_details?.firm_id || user?.firm_id;

  const [job,     setJob]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("overview");
  const [hasUpdate, setHasUpdate] = useState(false);

  // Remove modal
  const [showRemove, setShowRemove] = useState(false);
  const [removeNote, setRemoveNote] = useState("");
  const [removing,   setRemoving]   = useState(false);
  const [removeErr,  setRemoveErr]  = useState("");

  // Full-text modal
  const [readModal, setReadModal] = useState(null); // { title, text }

  useEffect(() => {
    if (!id || !userSlug) return;
    post("get-job-details", { job_slug: id, user_slug: userSlug }).then(data => {
      if (data?.job_details) setJob(data.job_details);
      setLoading(false);
    });
  }, [id, userSlug]);

  const canEdit      = job && String(user_id) === String(job.created_by);
  const hasAccess    = job && (String(job.firm_id) === String(firmId) || (job.job_firm_access||"").split(",").includes(String(firmId)));
  const hasAITab     = job && job.requirement_id !== null && job.is_submitted !== "0";

  const doRemove = async () => {
    if (!removeNote.trim()) { setRemoveErr("Please enter a reason"); return; }
    setRemoving(true); setRemoveErr("");
    const res = await post("remove-job", { user_slug: userSlug, job_slug: job.job_slug, reason: removeNote });
    setRemoving(false);
    if (res?.status === 1) { navigate("/jobs"); }
    else setRemoveErr(res?.message || "Failed to remove job.");
  };

  if (loading) {
    const Bone = ({ w="100%", h=14, radius=6, mb=0 }) => (
      <div style={{ width:w, height:h, borderRadius:radius, background:"linear-gradient(90deg,var(--fc-border-light) 25%,var(--fc-border) 50%,var(--fc-border-light) 75%)", backgroundSize:"200% 100%", animation:"fc-shimmer 1.4s infinite", marginBottom:mb }} />
    );
    return (
      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        <style>{`@keyframes fc-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>

        {/* Header card skeleton */}
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:"20px 22px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:14 }}>
            <div style={{ flex:1 }}>
              <Bone w="40%" h={11} mb={10} />
              <Bone w="65%" h={22} radius={8} mb={10} />
              <div style={{ display:"flex", gap:14 }}>
                <Bone w={90} h={10} />
                <Bone w={80} h={10} />
                <Bone w={100} h={10} />
              </div>
            </div>
            <Bone w={68} h={68} radius={12} />
          </div>
          {/* Stat cards row */}
          <div style={{ display:"flex", gap:10, marginTop:16, flexWrap:"wrap" }}>
            {[110,120,100,130,90].map((w,i) => <Bone key={i} w={w} h={56} radius={12} />)}
          </div>
        </div>

        {/* Tab bar skeleton */}
        <div style={{ display:"flex", gap:8, borderBottom:"2px solid var(--fc-border-light)", paddingBottom:0 }}>
          {[80,90,100].map((w,i) => <Bone key={i} w={w} h={32} radius={6} />)}
        </div>

        {/* Content card skeleton */}
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:"24px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <Bone w={32} h={32} radius={8} />
            <Bone w="30%" h={14} />
          </div>
          <Bone h={12} mb={8} />
          <Bone w="95%" h={12} mb={8} />
          <Bone w="88%" h={12} mb={8} />
          <Bone w="60%" h={12} mb={20} />

          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <Bone w={32} h={32} radius={8} />
            <Bone w="25%" h={14} />
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {[70,90,60,80,75,65].map((w,i) => <Bone key={i} w={w} h={28} radius={99} />)}
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, padding:"48px 24px", textAlign:"center" }}>
        <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)" }}>Job not found</div>
        <button onClick={() => navigate("/jobs")} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill" style={{marginTop:16}}>Back to jobs</button>
      </div>
    );
  }

  const skills = (job.required_skill_set||"").split(",").map(s=>s.trim()).filter(Boolean);

  return (
    <div>
      {/* ── Top bar: back + edit/remove ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10 }}>
        <button onClick={() => navigate(-1)}
          style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-text-muted)", fontSize:13, padding:0, display:"flex", alignItems:"center", gap:6, fontFamily:"inherit" }}>
          <i className="fa-solid fa-arrow-left" style={{fontSize:11}} /> Back to jobs
        </button>
        {canEdit && (
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => navigate(`/job/edit/${job.job_slug}`)}
              style={{ height:34, padding:"0 16px", borderRadius:9, border:"1px solid var(--fc-primary-border)", background:"var(--fc-primary-light)", color:"#2563EB", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}>
              <i className="fa-solid fa-pen" style={{fontSize:10}}/>Edit Job
            </button>
            <button onClick={() => { setShowRemove(true); setRemoveNote(""); setRemoveErr(""); }}
              style={{ height:34, padding:"0 16px", borderRadius:9, border:"1px solid var(--fc-red-border)", background:"var(--fc-red-light)", color:"#DC2626", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}>
              <i className="fa-solid fa-trash" style={{fontSize:10}}/>Remove
            </button>
          </div>
        )}
      </div>

      {/* ── Header card ── */}
      <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:18, padding:"22px 24px", marginBottom:14, boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>

        {/* Title + firm logo on same row */}
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16, marginBottom:18 }}>
          <div style={{ flex:1, minWidth:0 }}>
            {job.employer_name && <div style={{ fontSize:13, fontWeight:600, color:"var(--fc-text-muted)", marginBottom:4 }}>{job.employer_name}</div>}
            <h1 style={{ fontSize:22, fontWeight:800, color:"var(--fc-text-primary)", margin:"0 0 10px", lineHeight:1.25 }}>{job.job_title}</h1>
            <div style={{ display:"flex", flexWrap:"wrap", gap:14, fontSize:12, color:"var(--fc-text-subtle)", alignItems:"center" }}>
              {job.location_requirement && (() => {
                const loc = job.location_requirement.split(",").map(s=>s.trim()).filter(Boolean).join(", ");
                return loc ? <span style={{display:"flex",alignItems:"center",gap:4}}><i className="fa-solid fa-location-dot" style={{fontSize:10,color:"var(--fc-text-hint)"}}/>{loc}</span> : null;
              })()}
              {job.created_on && <span style={{display:"flex",alignItems:"center",gap:4}}><i className="fa-regular fa-clock" style={{fontSize:10}}/>{fmtDate(job.created_on)}</span>}
              {job.closing_date && <span style={{display:"flex",alignItems:"center",gap:4,color:"#DC2626"}}><i className="fa-solid fa-calendar-xmark" style={{fontSize:10}}/>Closes {fmtDate(job.closing_date)}</span>}
              {job.creator_name && <span style={{display:"flex",alignItems:"center",gap:4}}><i className="fa-solid fa-user" style={{fontSize:10,color:"var(--fc-text-hint)"}}/>Posted by {job.creator_name}</span>}
            </div>
          </div>
          {job.firm_logo && (
            <div style={{ width:68, height:68, borderRadius:14, background:"var(--fc-bg-light)", border:"1px solid var(--fc-border)", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", padding:5, flexShrink:0 }}>
              <img src={FIRM_IMG_BASE+job.firm_logo} alt="" style={{width:"100%",height:"100%",objectFit:"contain"}} onError={e=>e.target.parentElement.style.display="none"} />
            </div>
          )}
        </div>

        {/* ── Stat cards — fixed 4-col grid, only show available ── */}
        {[
          job.compensation && { label:"Salary", value:job.compensation, bg:"var(--fc-green-light)", color:"var(--fc-green-text)" },
          job.job_type    && { label:"Job Type", value:JOB_TYPE[job.job_type]||job.job_type, bg:"var(--fc-cyan-light)", color:"var(--fc-cyan-text)" },
          job.experience_required && { label:"Experience", value:job.experience_required, bg:"var(--fc-amber-light)", color:"var(--fc-amber-text)" },
          job.contract_length && { label:"Contract", value:job.contract_length, bg:"var(--fc-purple-light)", color:"var(--fc-purple-text)" },
          skills.length > 0 && { label:"Skills Required", value:`${skills.length} skills`, bg:"var(--fc-pink-light)", color:"#831843" },
          job.working_hours && { label:"Working Hours", value:job.working_hours, bg:"var(--fc-green-light)", color:"var(--fc-green-text)" },
        ].filter(Boolean).length > 0 && (
          <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
            {[
              job.compensation && { label:"Salary", value:job.compensation, bg:"var(--fc-green-light)", color:"var(--fc-green-text)" },
              job.job_type    && { label:"Job Type", value:JOB_TYPE[job.job_type]||job.job_type, bg:"var(--fc-cyan-light)", color:"var(--fc-cyan-text)" },
              job.experience_required && { label:"Experience", value:job.experience_required, bg:"var(--fc-amber-light)", color:"var(--fc-amber-text)" },
              job.contract_length && { label:"Contract", value:job.contract_length, bg:"var(--fc-purple-light)", color:"var(--fc-purple-text)" },
              skills.length > 0 && { label:"Skills Required", value:`${skills.length} skills`, bg:"var(--fc-pink-light)", color:"#831843" },
              job.working_hours && { label:"Working Hours", value:job.working_hours, bg:"var(--fc-green-light)", color:"var(--fc-green-text)" },
            ].filter(Boolean).map((stat,i) => (
              <div key={i} style={{ borderRadius:14, padding:"12px 18px", background:stat.bg, minWidth:130, flex:"1 1 130px", maxWidth:220 }}>
                <div style={{ fontSize:10, fontWeight:700, color:stat.color, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4, opacity:0.75 }}>{stat.label}</div>
                <div style={{ fontSize:16, fontWeight:800, color:stat.color, lineHeight:1.2 }}>{stat.value}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs — same style as profile page */}
      {hasAccess && (
        <div className="fc-profile-tabs" style={{ marginBottom:16 }}>
          {[
            { key:"overview",   label:"Overview",   icon:"fa-file-lines" },
            { key:"candidates", label:"Candidates", icon:"fa-users"      },
            ...(hasAITab ? [{ key:"ai", label:"AI Matching", icon:"fa-bolt" }] : []),
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`fc-profile-tab-btn${tab===t.key?" active":""}`}>
              <i className={`fa-solid ${t.icon}`} style={{fontSize:11}}/>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab content */}

        {/* Overview — no wrapper */}
        {tab === "overview" && (
          <JobOverviewTab job={job} skills={skills} />
        )}

        {/* Candidates */}
        {tab === "candidates" && (
          <JobCandidatesTab
            jobSlug={id} userSlug={userSlug} firmId={firmId}
            hasUpdate={hasUpdate}
            onUpdateDone={() => setHasUpdate(false)}
          />
        )}

        {/* AI Matching */}
        {tab === "ai" && hasAITab && (
          <JobAIMatchingTab jobSlug={id} userSlug={userSlug}
            onPropose={() => setHasUpdate(true)} />
        )}

      {/* Remove modal */}
      {showRemove && (
        <Modal title="Remove Job Posting" maxWidth={420}
          footer={
            <ModalFooter
              onCancel={()=>setShowRemove(false)}
              onConfirm={doRemove}
              confirmTone="danger"
              confirmBusy={removing}
              confirmLabel={removing ? "Removing..." : "Remove"}
            />
          }>
          <div style={{ fontSize:12, color:"var(--fc-text-subtle)", marginTop:-10, marginBottom:16 }}>Are you sure you want to remove "{job.job_title}"?</div>
          <label style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", display:"block", marginBottom:6, textTransform:"uppercase" }}>Reason *</label>
          <input value={removeNote} onChange={e=>{setRemoveNote(e.target.value);setRemoveErr("");}} placeholder="Enter reason for removal"
            disabled={removing}
            style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", border:`1.5px solid ${removeErr?"#DC2626":"var(--fc-border)"}`, borderRadius:9, fontSize:13, outline:"none", fontFamily:"inherit", background:"var(--fc-card-bg)", color:"var(--fc-text-primary)" }} />
          {removeErr && <div style={{ fontSize:11, color:"#DC2626", marginTop:4 }}>{removeErr}</div>}
        </Modal>
      )}
    </div>
  );
}